import React, { useState, useRef } from 'react';
import { useQuery } from 'react-query';
import { getInstagramAccount } from '../lib/instagram';
import InstagramLoginPopup from './InstagramLoginPopup';
import toast from 'react-hot-toast';
import { useClickOutside } from '../hooks/useClickOutside';

export default function SocialAccountsManager() {
  const [showDropdown, setShowDropdown] = useState(false);
  const [showInstagramPopup, setShowInstagramPopup] = useState(false);
  const { data: instagramAccount } = useQuery('instagramAccount', getInstagramAccount);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useClickOutside(dropdownRef, () => {
    setShowDropdown(false);
  });

  const handleConnect = (platform: string) => {
    if (platform === 'instagram') {
      setShowInstagramPopup(true);
      return;
    }
    
    if (!instagramAccount?.connected) {
      toast.error('Please connect Instagram first');
      return;
    }
    
    toast.error(`${platform} integration coming soon`);
  };

  const socialPlatforms = [
    { id: 'instagram', name: 'Instagram', icon: (
      <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
        <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
      </svg>
    )},
    { id: 'facebook', name: 'Facebook', icon: (
      <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
        <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
      </svg>
    )},
    { id: 'linkedin', name: 'LinkedIn', icon: (
      <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
        <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
      </svg>
    )},
    { id: 'tiktok', name: 'TikTok', icon: (
      <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
        <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z"/>
      </svg>
    )},
    { id: 'google', name: 'Google Business', icon: (
      <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
        <path d="M12 0C5.372 0 0 5.373 0 12s5.372 12 12 12 12-5.373 12-12S18.628 0 12 0zm6.804 16.864H5.196a2.196 2.196 0 01-2.196-2.196V9.196A2.196 2.196 0 015.196 7h13.608a2.196 2.196 0 012.196 2.196v5.472a2.196 2.196 0 01-2.196 2.196z"/>
      </svg>
    )}
  ];

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setShowDropdown(!showDropdown)}
        className="px-4 py-2 rounded-full text-sm font-medium text-gray-700 hover:bg-gray-50 flex items-center space-x-1"
      >
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
        <span>Manage</span>
      </button>

      {showDropdown && (
        <div className="absolute right-0 mt-2 w-72 rounded-lg bg-white shadow-lg ring-1 ring-black ring-opacity-5 z-50">
          <div className="p-4">
            <h3 className="text-sm font-medium text-gray-900 mb-3">Social Accounts</h3>
            <div className="space-y-2">
              {socialPlatforms.map((platform) => (
                <button
                  key={platform.id}
                  onClick={() => handleConnect(platform.id)}
                  disabled={platform.id !== 'instagram' && !instagramAccount?.connected}
                  className={`w-full flex items-center justify-between p-2 rounded-md text-sm ${
                    platform.id === 'instagram' || instagramAccount?.connected
                      ? 'hover:bg-gray-50'
                      : 'opacity-50 cursor-not-allowed'
                  }`}
                >
                  <div className="flex items-center space-x-3">
                    <div className={`${
                      platform.id === 'instagram' ? 'text-pink-500' :
                      platform.id === 'facebook' ? 'text-blue-600' :
                      platform.id === 'linkedin' ? 'text-blue-700' :
                      platform.id === 'tiktok' ? 'text-black' :
                      'text-red-500'
                    }`}>
                      {platform.icon}
                    </div>
                    <div className="flex flex-col items-start">
                      <span className="font-medium">{platform.name}</span>
                      {platform.id === 'instagram' && !instagramAccount?.connected && (
                        <span className="text-xs text-primary-500">Start here!</span>
                      )}
                    </div>
                  </div>
                  {platform.id === 'instagram' && instagramAccount?.connected ? (
                    <span className="text-green-500 text-xs font-medium">Connected</span>
                  ) : (
                    <span className="text-gray-500 text-xs">Connect</span>
                  )}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {showInstagramPopup && (
        <InstagramLoginPopup
          onClose={() => setShowInstagramPopup(false)}
          onSuccess={() => {
            setShowInstagramPopup(false);
            setShowDropdown(false);
          }}
        />
      )}
    </div>
  );
}